/**
 * EngagementMetrics.tsx
 *
 * Engagement stat tiles for a caller-supplied date range. Fetching goes
 * through useAPI()'s requestEngagementMetrics — currently a placeholder in
 * useAPI.ts, so this section will show its error state until that request
 * is implemented for real. Styling (.stat-tile, .kpi-row, .split-bar-block,
 * .status-row, etc.) is defined once in the parent AnalyticsDashboard's
 * <style> block.
 */
import { useEffect, useState } from 'react';
import useAPI from '../../Hooks/useAPI';

export interface EngagementMetrics {
    avgSessionDurationSeconds: string;
    avgPagesPerSession: string;
    singlePageSessionPct: string;
    pageExitRatePct: string;
}

// ---------------------------------------------------------------------
// Data fetch — via useAPI()'s requestEngagementMetrics
// ---------------------------------------------------------------------
function useEngagementMetrics(start: Date, end: Date) {
    const { requestEngagementMetrics } = useAPI();

    const [data, setData] = useState<EngagementMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        requestEngagementMetrics(start.toISOString(), end.toISOString())
            .then((response) => {
                if (cancelled) return;
                setData(response.data);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Failed to load engagement metrics:', err);
                setError(err instanceof Error ? err.message : 'Failed to load engagement metrics.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [start.getTime(), end.getTime(), requestEngagementMetrics]);

    return { data, loading, error };
}

// ---------------------------------------------------------------------
// Formatting — values arrive pre-formatted (duration, %, decimal) rather
// than raw counts, so they render directly rather than going through the
// compact-notation formatting the traffic tiles use.
// ---------------------------------------------------------------------
function formatDurationSeconds(secondsStr: string): string {
    const totalSeconds = Number(secondsStr);
    if (Number.isNaN(totalSeconds)) return secondsStr;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

function formatEngagementPct(pctStr: string): string {
    const n = Number(pctStr);
    if (Number.isNaN(n)) return pctStr;
    return `${n.toFixed(1)}%`;
}

function formatEngagementDecimal(valStr: string): string {
    const n = Number(valStr);
    if (Number.isNaN(n)) return valStr;
    return n.toFixed(2);
}

// ---------------------------------------------------------------------
// Stat tile — reuses the .stat-tile styling from the parent's style block
// ---------------------------------------------------------------------
function EngagementStatTile({ label, value }: { label: string; value: string }) {
    return (
        <div className="stat-tile">
            <div className="stat-tile-label">{label}</div>
            <div className="stat-tile-value">{value}</div>
        </div>
    );
}

// ---------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------
export default function EngagementMetricsSection({ start, end }: { start: Date; end: Date }) {
    const { data, loading, error } = useEngagementMetrics(start, end);

    return (
        <div className="split-bar-block engagement-block">
            <div className="split-bar-heading">Engagement</div>

            {loading && <div className="status-row">Loading…</div>}
            {error && <div className="status-row status-row--error">{error}</div>}

            {data && (
                <div className="kpi-row">
                    <EngagementStatTile label="Avg. session duration" value={formatDurationSeconds(data.avgSessionDurationSeconds)} />
                    <EngagementStatTile label="Pages per session" value={formatEngagementDecimal(data.avgPagesPerSession)} />
                    <EngagementStatTile label="Single-page session rate" value={formatEngagementPct(data.singlePageSessionPct)} />
                    <EngagementStatTile label="Page exit rate" value={formatEngagementPct(data.pageExitRatePct)} />
                </div>
            )}
        </div>
    );
}
